import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTrips } from '../hooks/useTrips'
import { useTripTypes } from '../hooks/useTripTypes'
import { useItems } from '../hooks/useItems'
import type { TripStatus, CreateTripInput } from '../types'

const statusConfig = {
  planning: { 
    label: 'Planning', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: '📋'
  },
  packing: { 
    label: 'Packing', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: '🎒'
  },
  'in-progress': { 
    label: 'In Progress', 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: '✈️'
  },
  completed: { 
    label: 'Completed', 
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: '✅'
  },
}

export default function Trips() {
  const { trips, getPackingStats, deleteTrip } = useTrips()
  const { tripTypes } = useTripTypes()
  const [showCreateForm, setShowCreateForm] = useState(false)

  const handleCreateTrip = () => {
    setShowCreateForm(true)
  }

  const handleFormCancel = () => {
    setShowCreateForm(false)
  }

  const getStatusBadge = (status: TripStatus) => {
    const config = statusConfig[status]
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config.color}`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    )
  }

  const getPackingProgress = (tripId: string) => {
    const stats = getPackingStats(tripId)
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${stats.percentage}%` }}
          />
        </div>
        <span className="text-xs text-gray-600 whitespace-nowrap">
          {stats.packed}/{stats.total} ({stats.percentage}%)
        </span>
      </div>
    )
  }

  const getTripTypeName = (tripTypeId: string) => {
    const tripType = tripTypes.find(tt => tt.id === tripTypeId)
    return tripType?.name || 'Unknown'
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">My Trips</h2>
        <button 
          onClick={handleCreateTrip}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Trip
        </button>
      </div>

      {showCreateForm && (
        <div className="mb-6">
          <CreateTripForm onCancel={handleFormCancel} />
        </div>
      )}

      {trips.length === 0 && !showCreateForm ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500 text-lg mb-4">No trips yet</p>
          <p className="text-gray-400">Create your first trip to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {trips.map((trip) => (
            <div
              key={trip.id}
              className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-medium text-gray-900">{trip.name}</h3>
                    {getStatusBadge(trip.status)}
                  </div>
                  {trip.description && (
                    <p className="text-gray-600 text-sm mb-2">{trip.description}</p>
                  )}
                  <p className="text-xs text-gray-400">
                    Based on {getTripTypeName(trip.tripTypeId)}
                    {trip.startDate && ` • ${trip.startDate.toLocaleDateString()}`}
                    {trip.endDate && trip.startDate && ` - ${trip.endDate.toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Link
                    to={`/trips/${trip.id}`}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    {trip.status === 'packing' ? 'Pack' : 'View'}
                  </Link>
                  <button 
                    onClick={() => deleteTrip(trip.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              {/* Packing Progress */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">Packing Progress</span>
                </div>
                {getPackingProgress(trip.id)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CreateTripForm({ onCancel }: { onCancel: () => void }) {
  const { createTripFromTripType } = useTrips()
  const { tripTypes } = useTripTypes()
  const { getItemsForTripType } = useItems()
  const [selectedTripTypeId, setSelectedTripTypeId] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!name.trim() || !selectedTripTypeId) return

    const tripTypeItems = getItemsForTripType(selectedTripTypeId)
    const tripInput: CreateTripInput = {
      name: name.trim(),
      description: description.trim() || undefined,
      tripTypeId: selectedTripTypeId,
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
    }

    createTripFromTripType(
      tripTypeItems.map(item => ({ itemId: item.id, priority: item.priority })),
      tripInput
    )

    onCancel()
  }

  const selectedTripType = tripTypes.find(tt => tt.id === selectedTripTypeId)
  const itemCount = selectedTripType ? getItemsForTripType(selectedTripTypeId).length : 0

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Trip</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="tripType" className="block text-sm font-medium text-gray-700 mb-1">
            Trip Type *
          </label>
          <select
            id="tripType"
            value={selectedTripTypeId}
            onChange={(e) => setSelectedTripTypeId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Choose a trip type...</option>
            {tripTypes.map((tripType) => (
              <option key={tripType.id} value={tripType.id}>
                {tripType.name}
              </option>
            ))}
          </select>
          {selectedTripType && (
            <p className="text-xs text-gray-500 mt-1">
              Will copy {itemCount} items from this trip type
            </p>
          )}
        </div>

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Trip Name *
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Weekend in the Alps"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={!name.trim() || !selectedTripTypeId}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Create Trip
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}